import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DEFAULT_SETTINGS, loadSettings, localToday } from "@/lib/organizer";

export function useOrganizer({ scope, studentId, focusId, onCounts }) {
  const storageKey = `ol_organizer_${scope}`;
  const [settings, setSettings] = useState(() => loadSettings(storageKey));
  const [page, setPage] = useState(1);
  const [weekStart, setWeekStart] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [allMatching, setAllMatching] = useState(false);

  useEffect(() => { localStorage.setItem(storageKey, JSON.stringify(settings)); }, [settings, storageKey]);

  const params = useMemo(() => {
    const query = { view: settings.view, page, page_size: 10, today: localToday() };
    const sid = studentId || settings.student_id;
    if (sid) query.student_id = sid;
    ["q", "subject", "status", "due_from", "due_to", "sort", "order"].forEach((key) => { if (settings[key]) query[key] = settings[key]; });
    if (settings.view === "all" && settings.include_archived) query.include_archived = true;
    if (settings.view === "this_week" && weekStart) query.week_start = weekStart;
    if (focusId) query.focus_id = focusId;
    return query;
  }, [settings, page, weekStart, studentId, focusId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await api.get("/parent/assignments/organizer", { params });
      setData(result); setError("");
      if (result.page !== page) setPage(result.page);
      onCounts?.(result.counts);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Assignments could not load.");
    } finally { setLoading(false); }
  }, [params, page, onCounts]);
  useEffect(() => { load(); }, [load]);

  const clearSelection = useCallback(() => { setSelected(new Set()); setAllMatching(false); }, []);
  const update = useCallback((patch) => {
    setSettings((current) => ({ ...current, ...patch }));
    setPage(1); clearSelection();
  }, [clearSelection]);
  const setView = useCallback((view) => update({ view, sort: "", order: "" }), [update]);
  const resetFilters = useCallback(() => update({ ...DEFAULT_SETTINGS, view: settings.view, student_id: settings.student_id }), [update, settings.view, settings.student_id]);

  const toggle = useCallback((id) => {
    setAllMatching(false);
    setSelected((current) => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }, []);
  const togglePage = useCallback((ids, checked) => {
    setAllMatching(false);
    setSelected((current) => { const next = new Set(current); ids.forEach((id) => checked ? next.add(id) : next.delete(id)); return next; });
  }, []);
  const selectAllMatching = useCallback(async () => {
    try {
      const { data: result } = await api.get("/parent/assignments/organizer", { params: { ...params, ids_only: true } });
      setSelected(new Set(result.ids)); setAllMatching(true);
    } catch { toast.error("Could not select all matching assignments"); }
  }, [params]);

  const bulk = useCallback(async (action, ids) => {
    try {
      const { data: result } = await api.post(`/assignments/bulk-${action}`, { ids });
      const count = result[action === "archive" ? "archived" : "restored"];
      toast.success(action === "archive" ? `${count} assignment${count === 1 ? "" : "s"} archived — records and grades kept` : `${count} assignment${count === 1 ? "" : "s"} restored`);
      clearSelection(); await load();
    } catch (requestError) { toast.error(requestError?.response?.data?.detail || "Bulk action failed"); }
  }, [clearSelection, load]);

  return {
    settings, data, loading, error, page, setPage, weekStart, setWeekStart, selected, allMatching,
    update, setView, resetFilters, toggle, togglePage, selectAllMatching, clearSelection, bulk, reload: load,
  };
}
